'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { StarRatingBar } from '@lastbrand/web-ui/star-rating-bar'
import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo, useState } from 'react'

import { ProductReviewSummaryDS } from '@src/components/blocks/product-review-summary-ds'
import { ReviewCreateModalDS } from '@src/components/blocks/review-create-modal-ds/dynamic'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetOfType } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { useLayoutContext, usePLPageMeta } from '@src/components/pl-page-context'
import { useIaImpressionRef, usePlPdpBundle, usePlPdpProduct } from '@src/components/widgets/use-ia-impression'
import { BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src'
import { MAX_REVIEW_HIGHLIGHTS } from '@src/quince-components/src/atoms/reviewHighLightsSection/reviewHighLightsSection.constants'
import { showReviewSection } from '@src/quince-components/src/atoms/reviewHighLightsSection/reviewHighLightsSection.helpers'
import reviewHighlightStyles from '@src/quince-components/src/atoms/reviewHighLightsSection/reviewHighlightsSectionV2.module.scss'
import ReviewHighlightsSummary from '@src/quince-components/src/atoms/reviewHighLightsSection/ReviewHighlightsSummary'
import { CreateReview } from '@src/quince-components/src/atoms/reviews/createReview/createReview'
import { ProductReviewSummary } from '@src/quince-components/src/atoms/reviews/ProductReviewSummary'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import { REVIEWS_SOURCES } from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.constant'
import reviewSectionStyles from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.module.scss'
import { TELEMETRY_EVENT_CATEGORIES } from '@src/telemetry/telemetry-events.constants'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'
import { toStarRatingValue } from '@src/utils/star-rating.utils'

import { adaptReviewOptionsToRanges, buildWriteReviewProduct } from '../shared/reviews.adapter'
import { EMPTY_PRODUCT_PROPERTIES } from '../shared/reviews.constants'
import { useReviewsVariant } from '../shared/reviews.context'
import { useIsBundlePdp } from '../shared/use-is-bundle-pdp'

import type { ReviewsHighlightWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Telemetry tokens — module-level consts (not inline literals) so they read as identifiers and stay
// clear of i18next/no-literal-string without suppression comments.
const ACCORDION_REVIEWS_ACTION = 'pdp_accordion_reviews'
const REVIEW_HIGHLIGHTS_SECTION_TYPE = 'Review Highlights'

const headerCss = css({ display: 'flex', flexDirection: 'column', gap: 'space-8' })
const summaryRowCss = css({ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'space-4' })
const ratingTextCss = css({ textStyle: 'body-14-strong', color: 'black' })
// Figma RatingPdp ReviewsAmount (Visual-QA 10155:25866): "(", the count, and ")" sit in a
// 2px-gap row, and the underscore is its own element at the count's text-box BOTTOM — below
// the descender line, unlike text-decoration which hugs the baseline (STRFRNT-16777).
// Twin of the non-PL organism's count styles (review-highlights-ds.tsx).
const countButtonCss = css({
  textStyle: 'title-12',
  color: 'black',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'space-2',
})
const countUnderlineCss = css({ display: 'inline-block', borderBottom: '1px solid token(colors.black)' })

// ---------------- REVIEWS_HIGHLIGHT (header: title + avg/count + bars) ----------------
function ReviewsHighlightWidgetComponent(props: PLTypedWidgetProps<ReviewsHighlightWidgetData>) {
  const { t } = useTranslation('common')
  const variant = useReviewsVariant()
  const reviewSystemExp = useReviewSystemV3Experiment()
  const isReviewSystemV3Enabled =
    reviewSystemExp.showReviewSummary ||
    reviewSystemExp.showReviewFilterTags ||
    reviewSystemExp.showReviewSummaryAndFilterTags
  const { isB2BSelfService } = useStorefrontModeContext()
  const [isCreateReviewOpen, setIsCreateReviewOpen] = useState(false)

  // Write-a-review: open the legacy CreateReview popup (which owns submission + the questionnaire
  // fetch, both keyed on product.id). The ONLY product data the popup needs is the product id, which
  // the PL layout response already carries at `context.pdp.productIds.productId` — NO enrich/product-service
  // call. Title feeds the popup header; the remaining Product fields the legacy component touches
  // (images/variants/dynamicImages for the header thumbnail) are passed as empty so nothing throws
  // and no extra fetch is made. Backend can later enrich imageUrl/variants here for the thumbnail.
  const layoutContext = useLayoutContext()
  const pageMeta = usePLPageMeta()
  const productId = layoutContext?.pdp?.productIds?.productId
  const writeReviewProduct = useMemo(
    () =>
      productId != null ? buildWriteReviewProduct(productId, pageMeta.title ?? '', pageMeta.seoData?.imageUrl) : null,
    [productId, pageMeta.title, pageMeta.seoData?.imageUrl],
  )

  const openCreateReviewPopUp = useCallback(() => {
    setIsCreateReviewOpen(true)
  }, [])

  // Bundles hide the write-a-review CTA — non-PL parity (`product-reviews-legacy` passes
  // `hideWriteReviewButton={product.type === PRODUCT_TYPES.BUNDLE …}`): a review targets a real
  // product (a bundle child), and the popup's `productId` here would be the bundle's.
  const hideWriteReviewButton = useIsBundlePdp()

  // IA-only `pdp_accordion_reviews` impression when the mini "Review Highlights" band scrolls into view —
  // mirrors the non-PL review-highlights section (`entity_id: pdp_accordion_reviews`,
  // `review_section_type: 'Review Highlights'`), emitted InternalAnalytics-only per the PL directive.
  // Attached to the mini band's outer wrapper below; the FULL surface (a different review section that
  // owns `pdp_reviews`) never attaches it, so it fires only for the mini highlights band.
  const shouldShowReviewSummary = reviewSystemExp.showReviewSummary || reviewSystemExp.showReviewSummaryAndFilterTags
  const highlightCount = props.data.reviewsHighlightData?.count ?? 0
  // Non-PL sends the domain product title (`product.title` in ReviewHighLightsSection / review-highlights-ds),
  // not the SEO page `<title>` (`pageMeta.title`), so rebuild the domain product here for parity.
  const plProduct = usePlPdpProduct()
  // Bundle page: non-PL resolves `product = props.bundle` here, so the impression carries the
  // BUNDLE's spid + title (`ReviewHighLightsSection` receives the bundle as its `product`).
  const plBundle = usePlPdpBundle()
  // Non-PL keys BOTH events this widget emits on `product.spid` — `pdp_accordion_reviews`
  // (`productId: product.spid`) and `click_write_review` (`product_id: product.spid`) — NOT `product.id`.
  // The adapter degrades a proto-omitted spid to 0 (bundle: ''), so fall back to the layout-context
  // product id then.
  // (The sibling reviews-list `pdp_reviews` stays on `product.id` — that is what non-PL sends there.)
  const reviewsProductSpid = plProduct?.spid || plBundle?.spid || productId
  // Non-PL `review_impression_count` is the number of highlight snippets ACTUALLY rendered (capped at
  // MAX_REVIEW_HIGHLIGHTS), which live in the sibling REVIEWS_LIST (mini) widget — NOT the product's TOTAL
  // review count (`reviewsHighlightData.count`). Read the list items the same way reviews-list reads this
  // widget's count; fall back to the total clamp while the lazy list widget is absent.
  const highlightListItems = useWidgetOfType(WidgetType.REVIEWS_LIST)?.widget.data?.reviewsListData?.items
  const reviewImpressionCount = Math.min(highlightListItems?.length ?? highlightCount, MAX_REVIEW_HIGHLIGHTS)
  const getAccordionReviewsImpressionParams = useCallback(
    () => ({
      action: ACCORDION_REVIEWS_ACTION,
      context: {
        productId: reviewsProductSpid,
        productName: plProduct?.title ?? plBundle?.title ?? pageMeta.title,
        ...(shouldShowReviewSummary && {
          review_impression_count: reviewImpressionCount,
          review_section_type: REVIEW_HIGHLIGHTS_SECTION_TYPE,
        }),
      },
    }),
    [
      reviewsProductSpid,
      plProduct?.title,
      plBundle?.title,
      pageMeta.title,
      shouldShowReviewSummary,
      reviewImpressionCount,
    ],
  )
  const accordionReviewsImpressionRef = useIaImpressionRef(getAccordionReviewsImpressionParams)

  const data = props.data.reviewsHighlightData
  if (!data) return null
  // The rating-range bars belong to the FULL reviews summary only. The mini "Review Highlights"
  // surface shows just the heading + avg/count (matches the legacy ReviewHighLightsSection), even
  // though the backend ships `options` in both surfaces.
  // FULL surface: the dedicated ProductReviewSummary — big rating + dual-label range sliders
  // (minLabel ↔ maxLabel) + write-a-review — matching the legacy full-reviews summary.
  if (variant === 'full') {
    if (shouldUseDesignSystem()) {
      return (
        <>
          <ProductReviewSummaryDS
            avgRating={data.average}
            reviewsCount={data.count}
            ranges={adaptReviewOptionsToRanges(data.options)}
            openCreateReviewPopUp={openCreateReviewPopUp}
            trackConfig={{
              trackGA: true,
              trackInternal: true,
              category: TELEMETRY_EVENT_CATEGORIES.REVIEWS,
              action: 'click_write_review',
              context: { product_id: reviewsProductSpid },
            }}
            isReviewSystemV3Enabled={isReviewSystemV3Enabled}
            showReviewSummaryAndFilterTags={reviewSystemExp.showReviewSummaryAndFilterTags}
            hideWriteReviewButton={hideWriteReviewButton}
          />
          {/* DS create-review modal (owns submission), same minimal product as the legacy branch. */}
          {writeReviewProduct && !hideWriteReviewButton && (
            <ReviewCreateModalDS
              show={isCreateReviewOpen}
              onCancel={() => {
                setIsCreateReviewOpen(false)
              }}
              product={writeReviewProduct}
              reviewSource={REVIEWS_SOURCES.PDP}
              isB2BSelfService={isB2BSelfService}
            />
          )}
        </>
      )
    }
    return (
      <>
        <ProductReviewSummary
          // Same section padding/margins the non-PL organism applies (productReviews.module.scss):
          // base + newPdpv2 + reviewSystemExp modifiers — without these the mobile sliders lose their
          // horizontal padding and labels run to the screen edges.
          className={classnames(
            reviewSectionStyles.productReviewsSummary,
            reviewSectionStyles.productReviewsSummary_newPdpv2,
            {
              [reviewSectionStyles.productReviewsSummary_newPdpv2_reviewSystemExp ?? '']:
                reviewSystemExp.showReviewSummaryAndFilterTags,
            },
          )}
          avgRating={data.average}
          reviewsCount={data.count}
          ranges={data.options.map(option => ({
            title: option.name,
            fillPercentage: option.percent,
            minLabel: option.options[0] ?? '',
            maxLabel: option.options.slice(-1)[0] ?? '',
          }))}
          // Match non-PL: the write-a-review CTA is an OUTLINED/MEDIUM button (not the filled coral).
          writeReviewButton={{
            type: BUTTON_TYPES.OUTLINED,
            size: BUTTON_SIZE.MEDIUM,
            children: t('write_a_review'),
          }}
          // Fire `click_write_review` (BUTTON_CLICK) on the legacy branch too — the atom only tracks
          // when trackConfig is present, and without this the event fired only on the DS branch.
          // Matches the DS branch above and the non-PL product-reviews-legacy summary.
          trackConfig={{
            trackGA: true,
            trackInternal: true,
            category: TELEMETRY_EVENT_CATEGORIES.REVIEWS,
            action: 'click_write_review',
            context: { product_id: reviewsProductSpid },
          }}
          openCreateReviewPopUp={openCreateReviewPopUp}
          isReviewSystemV3Enabled
          hideWriteReviewButton={hideWriteReviewButton}
        />
        {/* Legacy create-review popup (owns submission). Rendered once the product data is available
            from the shared context (populated from widget data — no enrich call). */}
        {writeReviewProduct && !hideWriteReviewButton && (
          <CreateReview
            show={isCreateReviewOpen}
            onCancel={() => {
              setIsCreateReviewOpen(false)
            }}
            product={writeReviewProduct}
            reviewSource={REVIEWS_SOURCES.PDP}
            isB2BSelfService={isB2BSelfService}
            productProperties={EMPTY_PRODUCT_PROPERTIES}
          />
        )}
      </>
    )
  }

  // MINI surface: condensed "Review Highlights" header (no rating bars).
  if (shouldUseDesignSystem()) {
    const avgStarValue = toStarRatingValue(data.average)
    return (
      <div ref={accordionReviewsImpressionRef} className={headerCss}>
        <h2 className={css({ textStyle: 'title-14', color: 'black' })}>{data.title ?? t('review_highlights')}</h2>
        <div className={summaryRowCss}>
          <StarRatingBar
            value={avgStarValue}
            variant="cantaloupe"
            aria-label={t('out_of_5_stars', { rating: parseFloat(avgStarValue) })}
          />
          <span className={ratingTextCss}>{getFormattedAverageRating(data.average)}</span>
          <button
            type="button"
            className={countButtonCss}
            onClick={showReviewSection}
            aria-label={`${data.count} ${t('reviews')}, ${t('see_all_reviews')}`}
          >
            {/* eslint-disable-next-line i18next/no-literal-string -- punctuation, not copy */}
            <span>(</span>
            <span className={countUnderlineCss}>{data.count}</span>
            {/* eslint-disable-next-line i18next/no-literal-string -- punctuation, not copy */}
            <span>)</span>
          </button>
        </div>
      </div>
    )
  }
  return (
    <div
      ref={accordionReviewsImpressionRef}
      className={classnames(
        reviewHighlightStyles.container__wrapper__header,
        reviewHighlightStyles.container__wrapper__header__withSummary,
      )}
    >
      <span className={reviewHighlightStyles.container__wrapper__header__title}>
        {(data.title ?? t('review_highlights')).toUpperCase()}
      </span>
      <ReviewHighlightsSummary
        averageRating={data.average}
        reviewCount={data.count}
        showReviewSection={showReviewSection}
      />
    </div>
  )
}

export const ReviewsHighlightWidget: PLTypedWidgetInterface<ReviewsHighlightWidgetData, WidgetType.REVIEWS_HIGHLIGHT> =
  {
    id: WidgetType.REVIEWS_HIGHLIGHT,
    errorBoundary: PLBaseErrorBoundary,
    render: ReviewsHighlightWidgetComponent,
  }
